import { FC, Fragment, createPortal } from "preact/compat";
import { Transition } from "@headlessui/react";
import { useComputed } from "@preact/signals";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { TIME_COLORS, getColorFor } from "~/core/color-manager";
import { me, useApp } from "~/core/store";
import { IconButton } from "~/components";
import { formatMinutes } from "~/utils";
import { useMobileDashboard } from "../../context";
import { PORTAL_TARGET_ID } from "../../utils";
import { PopupProps } from "./interfaces";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import cn from "classnames";

export const POPUP_WIDTH = 180;
export const POPUP_HEIGHT = 156;

const LEGEND = [
    {
        color: TIME_COLORS.contracted,
        label: "Contracted",
    },
    {
        color: TIME_COLORS.tracked,
        label: "Tracked",
    },
    {
        color: TIME_COLORS.reported,
        label: "Reported",
    },
];
const OVERFLOW_COLOR = TIME_COLORS.overReported;
const SELECTED_INDEX = 3;

const Popup: FC<PopupProps> = ({ show, coords, date, onClose }) => {
    const targetElement = document.getElementById(PORTAL_TARGET_ID);
    const { api } = useApp();
    const { overviewData, selectedProjectId, selectedFeeCodeId } =
        useMobileDashboard();
    const extra = useComputed(() => {
        const project = api.project.get(selectedProjectId.value);
        const feecode = api.feeCode.get(selectedFeeCodeId.value);

        if (!project && !feecode) {
            return null;
        }

        let name = "";
        let color1 = "";
        let color2 = "";

        if (project && feecode) {
            name = `${feecode.name_global} @ ${project.name}`;
            color1 = getColorFor("project", project.id);
            color2 = getColorFor("feecode", feecode.id);
        } else if (project) {
            name = project.name;
            color1 = getColorFor("project", project.id);
        } else if (feecode) {
            name = feecode.name_global;
            color1 = getColorFor("feecode", feecode.id);
        }

        return { name, color1, color2 };
    });

    if (!targetElement) {
        return null;
    }

    const dayStr = date ? format(date, "yyyy-MM-dd") : "";
    const data = overviewData.value;
    const values = data.values[(date?.getDate() ?? 1) - 1] ?? [0, 0, 0, 0];
    const [contracted, tracked, totalReported] = values;
    const overflow =
        contracted > 0 && totalReported > contracted
            ? totalReported % contracted || contracted
            : 0;
    const reported = totalReported > contracted ? contracted : totalReported;
    const max = Math.max(contracted, tracked, reported, overflow);
    const bars =
        max > 0
            ? ([
                  [tracked, LEGEND[1].color],
                  [reported, LEGEND[2].color],
                  [overflow, OVERFLOW_COLOR],
              ] as const)
            : [];
    const extraBar = extra.value;

    return createPortal(
        <Transition
            show={show.value}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
        >
            <div
                data-cell-popup
                style={{
                    width: `${POPUP_WIDTH}px`,
                    height: `${POPUP_HEIGHT}px`,
                    top: `${coords.value.y}px`,
                    left: `${coords.value.x}px`,
                }}
                className="absolute z-20 bg-white rounded-2xl border border-gray-300 shadow-lg overflow-y-auto text-sm transition-all duration-200"
            >
                <div className="p-4">
                    <div className="flex justify-between items-center mb-1">
                        <h3 className="font-bold text-gray-500">
                            {date ? format(date, "dd MMM, EEE") : ""}
                        </h3>
                        <IconButton flat title="Close popup" onClick={onClose}>
                            <CloseIcon width="16" height="16" />
                        </IconButton>
                    </div>
                    <div
                        className={cn(
                            "w-full rounded-lg h-2 relative",
                            contracted > 0 && "bg-gray-200",
                        )}
                    >
                        {bars.map(([value, color]) => (
                            <div
                                key={color}
                                style={{
                                    width: `${(value / max) * 100}%`,
                                    backgroundColor: color,
                                }}
                                className="absolute left-0 top-0 h-full rounded-lg transition-all duration-300"
                            />
                        ))}
                    </div>
                </div>
                <ul className="flex flex-col gap-y-1 text-sm px-4">
                    {extraBar && (
                        <li className="mb-2 flex flex-col gap-y-1 min-h-[3.25rem]">
                            <div className="flex items-center gap-x-2">
                                {extraBar.color1 && extraBar.color2 ? (
                                    <div className="flex flex-shrink-0 mt-1">
                                        <div
                                            className="h-2 w-1 rounded-l-full"
                                            style={{
                                                backgroundColor:
                                                    extraBar.color1,
                                            }}
                                        />
                                        <div
                                            className="h-2 w-1 rounded-r-full"
                                            style={{
                                                backgroundColor:
                                                    extraBar.color2,
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            backgroundColor:
                                                extraBar.color1 ||
                                                extraBar.color2,
                                        }}
                                        className="size-2 flex-shrink-0 rounded-full mt-1"
                                    />
                                )}
                                <span className="font-bold flex-shrink-0">
                                    {formatMinutes(values[SELECTED_INDEX] * 60)}
                                </span>
                            </div>
                            <div className="text-xs break-all">
                                {extraBar.name}
                            </div>
                        </li>
                    )}
                    {!extraBar &&
                        LEGEND.map((legend, index) => (
                            <li
                                key={legend.label}
                                className="flex justify-between items-center"
                            >
                                <div className="flex items-center gap-x-1">
                                    <div
                                        style={{
                                            backgroundColor: legend.color,
                                        }}
                                        className="size-2 flex-shrink-0 rounded"
                                    />
                                    <span>{legend.label}</span>
                                </div>
                                <span className="font-bold">
                                    {formatMinutes(values[index] * 60)}
                                </span>
                            </li>
                        ))}
                </ul>
                <Link
                    to={me(
                        `admin/time?calendar=month&date=${dayStr}&sidebar=${dayStr}`,
                    )}
                    className="px-4 py-1 block text-center text-sky-700 font-bold mt-1"
                >
                    Open in Calendar
                </Link>
            </div>
        </Transition>,
        targetElement,
    );
};

export default Popup;
